<template>
  <div class="mt-paso">
    <!-- Trámites que se pueden firmar por videoconferencia: va primero y marcada; la provincia solo si elige firmar en persona.
         Ref: https://trello.com/c/SDc9kpZG, https://trello.com/c/pooUEbKK -->
    <template v-if="admiteVideoconferencia">
      <p class="sw-intro">{{ t('Este trámite se puede firmar por videoconferencia, sin desplazarse. ¿Cómo prefiere firmar?') }}</p>
      <div class="sw-opciones sw-opciones--2 mb-4" role="radiogroup" :aria-label="t('Cómo prefiere firmar')">
        <button type="button" class="sw-opcion" role="radio" :aria-checked="videoconferencia"
          :class="{ activo: videoconferencia }" @click="elegirModalidad(true)">
          <v-icon class="sw-opcion__icono">mdi-video-outline</v-icon>
          <span class="sw-opcion__titulo">{{ t('Por videoconferencia') }} <span class="mt-recomendado">{{ t('Recomendado') }}</span></span>
          <span class="sw-opcion__texto">{{ t('Sin desplazarse: puede elegir cualquier notaría de España que la ofrezca.') }}</span>
        </button>
        <button type="button" class="sw-opcion" role="radio" :aria-checked="!videoconferencia"
          :class="{ activo: !videoconferencia }" @click="elegirModalidad(false)">
          <v-icon class="sw-opcion__icono">mdi-map-marker-outline</v-icon>
          <span class="sw-opcion__titulo">{{ t('En persona, en una notaría') }}</span>
          <span class="sw-opcion__texto">{{ t('Elija una notaría cerca de usted y firme allí.') }}</span>
        </button>
      </div>
    </template>

    <p class="sw-intro">
      {{ videoconferencia
        ? t('Elija la notaría que prefiera entre las que firman por videoconferencia. Si no tiene preferencia, nosotros le asignaremos una.')
        : t('Indique dónde quiere firmar y elija la notaría que prefiera. Si no tiene preferencia, nosotros le asignaremos una.') }}
    </p>
    <!-- Próxima fecha para firmar: solo en persona y si alguna notaría de la lista la enseña. Ref: https://trello.com/c/quriKGDT -->
    <p v-if="hayFechas" class="sw-ayuda">{{ t('Junto a cada notaría verá, si la publica, su próxima fecha disponible para firmar.') }}</p>
    <v-row v-if="!videoconferencia" dense>
      <v-col cols="12" sm="6">
        <v-autocomplete v-model="provincia" :items="PROVINCIAS" item-title="nombre" item-value="codigo"
          :label="t('Provincia')" variant="outlined" density="comfortable"
          autocomplete="off" :no-data-text="t('Sin resultados')" />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field v-model="localidad" :label="t('Localidad (opcional)')" variant="outlined" density="comfortable"
          maxlength="80" autocomplete="address-level2" @keyup.enter="buscar" @blur="buscarSiCambia" />
      </v-col>
    </v-row>

    <div v-if="cargando" class="sw-cargando" role="status">
      <v-progress-circular indeterminate size="22" width="2" class="mr-3" />
      {{ t('Buscando notarías…') }}
    </div>
    <p v-else-if="fallo" class="sw-error">{{ t('No se han podido cargar las notarías. Puede continuar sin preferencia.') }}</p>

    <div v-if="hayBusqueda && !cargando && notarias.length === 0 && !fallo" class="mt-aviso-sin-notarias my-2">
      <v-alert type="info" variant="tonal" density="comfortable" class="text-body-2 mb-2">
        {{ t('Aún no hay notarías para elegir directamente aquí, pero trabajamos con más de {n} en toda España. Elija «No tengo preferencia» y nos encargamos.', { n: RED_NOTARIAS }) }}
      </v-alert>
    </div>

    <div v-if="hayBusqueda && !cargando" class="sw-opciones sw-opciones--2 mt-2" role="radiogroup" :aria-label="t('Notaría')">
      <button v-for="n in notarias" :key="n.id" type="button" class="sw-opcion" role="radio"
        :aria-checked="destino?.id === n.id" :class="{ activo: destino?.id === n.id }" @click="elegir(n)">
        <v-icon class="sw-opcion__icono">mdi-bank-outline</v-icon>
        <span class="sw-opcion__titulo">{{ n.nombre }}</span>
        <span class="sw-opcion__texto">{{ direccion(n) }}</span>
        <template v-if="hayFechas">
          <span v-if="textoFechaFirma(n.proximaFecha)" class="sw-opcion__texto mt-fecha-firma"
            :aria-label="t('Próxima fecha disponible para firmar: {fecha}', { fecha: textoFechaFirma(n.proximaFecha) })">
            <v-icon size="16" class="mr-1" aria-hidden="true">mdi-calendar-check-outline</v-icon>
            <template v-if="esManana(n.proximaFecha)">{{ t('Puede firmar a partir de mañana') }}</template>
            <template v-else>
              <span class="d-none d-sm-inline">{{ t('Próxima fecha para firmar: {fecha}', { fecha: textoFechaFirma(n.proximaFecha) }) }}</span>
              <span class="d-sm-none">{{ t('Firma desde el {fecha}', { fecha: textoFechaFirma(n.proximaFecha, true) }) }}</span>
            </template>
          </span>
          <span v-else class="sw-opcion__texto sw-tenue">{{ t('La notaría le propondrá fecha al revisar su encargo') }}</span>
        </template>
      </button>
      <button type="button" class="sw-opcion" role="radio"
        :aria-checked="destinoElegido && !destino" :class="{ activo: destinoElegido && !destino }" @click="elegir(null)">
        <v-icon class="sw-opcion__icono">mdi-shuffle-variant</v-icon>
        <span class="sw-opcion__titulo">{{ t('No tengo preferencia') }}</span>
        <span class="sw-opcion__texto">{{ t('Gestionamos su encargo con nuestra red de más de {n} notarías y le contactamos.', { n: RED_NOTARIAS }) }}</span>
      </button>
    </div>
    <p v-if="hayFechas" class="sw-ayuda mt-3">
      {{ t('Las fechas son orientativas. La notaría le confirmará el día y la hora al revisar su encargo, o le propondrá otra fecha.') }}
    </p>
    <p v-if="videoconferencia && hayBusqueda && !cargando" class="sw-ayuda mt-3">
      {{ t('La firma se hace a través del portal oficial del notariado. La notaría le confirmará si su caso puede firmarse por videoconferencia y le explicará los pasos.') }}
    </p>
    <p v-if="errores['destino']" class="sw-error">{{ errores['destino'] }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { t } from '../../composables/solicitudTextos'
import { useSolicitudWeb } from '../../composables/useSolicitudWeb'
import { buscarNotariasMinotaria, esManana, textoFechaFirma, type NotariaMinotaria } from '../../composables/useMinotaria'
import { PROVINCIAS } from '../../data/provincias'
import { RED_NOTARIAS } from '../../plantillas/minotaria'

const { estado, errores, destino, destinoElegido, elegirDestino, siguiente, pasoActual, tipos } = useSolicitudWeb()

// La búsqueda se recuerda mientras dure la sesión, para volver a este paso sin repetirla
const provincia = useState<string | null>('minotariaProvincia', () => null)
const localidad = useState<string>('minotariaLocalidad', () => '')
/** El ciudadano quiere firmar por videoconferencia (solo en los trámites que lo admiten). Viaja en el encargo. */
const videoconferencia = computed<boolean>({
  get: () => !!estado.value.firmaVideoconferencia,
  set: (v) => { estado.value.firmaVideoconferencia = v },
})
const notarias = ref<NotariaMinotaria[]>([])
const cargando = ref(false)
const fallo = ref(false)
let ultimaBusqueda = ''
let temporizador: ReturnType<typeof setTimeout> | null = null
let temporizadorLocalidad: ReturnType<typeof setTimeout> | null = null

const admiteVideoconferencia = computed(() =>
  !!tipos.value.find((x) => x.codigo === estado.value.tipoOperacion)?.admiteVideoconferencia)

/** Con videoconferencia se lista sin provincia (no se pide); en persona hace falta la provincia. */
const hayBusqueda = computed(() => videoconferencia.value || !!provincia.value)

/** Alguna notaría de la lista enseña su próxima fecha (solo en persona; en videoconferencia no se muestra). */
const hayFechas = computed(() => !videoconferencia.value && hayBusqueda.value && !cargando.value
  && notarias.value.some((n) => !!textoFechaFirma(n.proximaFecha)))

async function buscar() {
  if (!estado.value.tipoOperacion || !hayBusqueda.value) return
  const clave = [estado.value.tipoOperacion, videoconferencia.value ? 'video' : 'zona', videoconferencia.value ? '' : provincia.value ?? '',
    videoconferencia.value ? '' : localidad.value.trim()].join('|')
  if (clave === ultimaBusqueda) return
  ultimaBusqueda = clave
  cargando.value = true
  fallo.value = false
  try {
    notarias.value = await buscarNotariasMinotaria(estado.value.tipoOperacion, videoconferencia.value ? null : provincia.value,
      videoconferencia.value ? '' : localidad.value, videoconferencia.value)
  } catch {
    notarias.value = []
    fallo.value = true
    ultimaBusqueda = ''
  } finally {
    cargando.value = false
  }
  // Si la notaría elegida ya no está en la lista, se deshace la elección; si sigue, se actualiza su fecha
  if (destino.value) {
    const sigue = notarias.value.find((n) => n.id === destino.value!.id)
    if (!sigue) elegirDestino(null, false)
    else elegirDestino(aDestino(sigue), destinoElegido.value)
  }
}

function buscarSiCambia() { buscar() }

function elegirModalidad(video: boolean) {
  videoconferencia.value = video
}

// Otro trámite que no admite videoconferencia: se vuelve a la notaría de la zona
watch(admiteVideoconferencia, (admite) => { if (!admite && videoconferencia.value) videoconferencia.value = false }, { immediate: true })

watch(provincia, () => buscar(), { immediate: true })
watch(videoconferencia, () => buscar())

watch(localidad, (val) => {
  if (!provincia.value && val && val.trim()) {
    const normalizado = val.trim().toLowerCase()
    const encontrada = PROVINCIAS.find((p) => p.nombre.toLowerCase() === normalizado)
    if (encontrada) provincia.value = encontrada.codigo
  }
  if (temporizadorLocalidad) clearTimeout(temporizadorLocalidad)
  temporizadorLocalidad = setTimeout(() => {
    buscar()
  }, 350)
})

function direccion(n: NotariaMinotaria): string {
  const texto = [n.direccion, [n.cp, n.municipio].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  // En videoconferencia salen notarías de toda España: se añade la provincia si no aparece ya
  if (videoconferencia.value && n.provincia && !texto.toLowerCase().includes(n.provincia.toLowerCase())) return [texto, n.provincia].filter(Boolean).join(' · ')
  return texto
}

function aDestino(n: NotariaMinotaria) {
  return { id: n.id, nombre: n.nombre, recaptchaGroup: n.recaptchaGroup, proximaFecha: n.proximaFecha ?? null }
}

/** Igual que en el paso del trámite: al elegir se avanza tras una pausa breve. */
function elegir(n: NotariaMinotaria | null) {
  elegirDestino(n ? aDestino(n) : null, true)
  if (temporizador) clearTimeout(temporizador)
  temporizador = setTimeout(() => { if (pasoActual.value === 'destino') siguiente() }, 280)
}
</script>
